import { Box, Flex, font, themedColor } from '@lemonade-hq/cdk';
import { Button, CheckmarkIcon, InfoIcon, InputText } from '@lemonade-hq/nuis';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis-atomic';
import { Client } from '@lemonade-hq/nuiza';
import { isValidEmail } from '@lemonade-hq/ts-helpers';
import { useState } from 'react';
import styled from 'styled-components';
import { SmallTitle } from './shared';

const Wrapper = styled(Flex)`
  ${font('main')}
  padding: 24px;
  border-radius: 10px;
  flex-direction: column;
  gap: 6px;
  font-size: 1.6rem;
  background: ${themedColor('cloud')};
  width: min(100%, 1280px);
  margin: 0 auto;

  ${MEDIA_QUERIES.desktop`
    padding: 60px 20px 60px 260px;
    border-radius: 0;
  `}

  ${MEDIA_QUERIES.tablet`
    padding: 60px 20px 60px 220px;
    border-radius: 0;

  `}
`;

const Title = styled(SmallTitle)`
  font-size: 2rem;
`;

const StyleButton = styled(Button)`
  padding-inline: 30px;
  height: 51px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    color: ${themedColor('textPrimary')};
  }
`;

const StyledInput = styled(InputText)`
  width: 100%;
  height: 51px;
  input {
    height: 100%;
  }
`;

const Form = styled.form`
  display: flex;
  position: relative;
  gap: 8px;
  margin-top: 8px;
  width: min(100%, 550px);
  flex-direction: column;

  ${MEDIA_QUERIES.tabletAndDesktop`
    flex-direction: row;
    `}
`;

const Content = styled.p`
  font-size: 1.6rem;
  line-height: 2.4rem;
  text-wrap: pretty;
`;

const Container = styled(Flex)`
  background: white;
  width: 100%;
  justify-content: center;

  ${MEDIA_QUERIES.tabletAndDesktop`
    background: ${themedColor('cloud')};
  `}
`;

const ErrorMesssage = styled.p`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${themedColor('primary')};
  font-size: 1.4rem;
  line-height: 2rem;
`;

const subscribeEmail = async (email: string) => {
  return await Client.post<{ message: string }>(`${process.env.NEXT_PUBLIC_INVESTORS_URL}/subscribe`, {
    email,
  });
};
export const SubcritionForm = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isLoading) return;

    if (email.trim() === '') {
      return setError('Please fill out your email');
    }

    if (!isValidEmail(email)) {
      return setError("This doesn't look like a valid email");
    }

    setIsLoading(true);
    try {
      const res = await subscribeEmail(email);
      setEmail('');
      setMessage(res.message);
      setIsLoading(false);
      setMessage("Your'e in");
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      setError('Upps error occurred, try again later');
      setEmail('');
    }
  };

  return (
    <>
      <Form onSubmit={handleSubmit}>
        <StyledInput
          floatingLabel={true}
          placeholder="Email address"
          onChange={e => {
            setEmail(e.target.value);
            setError('');
          }}
          type="text"
          value={email}
          disabled={isLoading}
        />
        <StyleButton flavor="primary" glowing disabled={message.length > 0} isLoading={isLoading}>
          {message.length > 0 ? (
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <CheckmarkIcon colorOverride="#4a4a4a" style={{ marginLeft: '-15px' }} />
              {message}
            </span>
          ) : (
            'Subscribe'
          )}
        </StyleButton>
      </Form>
      <Box height="20px">
        {error && (
          <ErrorMesssage>
            <InfoIcon colorOverride={'#ff0083'} />
            {error}
          </ErrorMesssage>
        )}
      </Box>
    </>
  );
};

interface SubscriptionProps {
  title?: string;
  content?: string;
  email?: string;
}
export const Subscription: React.FC<SubscriptionProps> = ({
  title = `Don't miss an investor update!`,
  content = 'Sign up for the latest news and reports delivered to your inbox',
  email,
}) => {
  return (
    <Container pl={[20, 10, 0]} pr={[20, 0, 0]}>
      <Wrapper>
        <Title>{title}</Title>
        <Content>{content}</Content>
        <SubcritionForm />

        {email && (
          <p>
            Contact us at <a href={`mailto:${email}`}>{email}</a>
          </p>
        )}
      </Wrapper>
    </Container>
  );
};
